import React from 'react';
import { VirtualTourConfig } from '../types/profile';
import { useLocalization } from '../engine/localization/context';
import { ExternalLink, Sparkles } from 'lucide-react';

interface VirtualTourCtaProps {
  virtualTour?: VirtualTourConfig;
}

export const VirtualTourCta: React.FC<VirtualTourCtaProps> = ({ virtualTour }) => {
  const { resolve, t } = useLocalization();

  if (!virtualTour) return null;

  const isActiveTour = Boolean(virtualTour.enabled && virtualTour.url);
  const isPromo = Boolean(!isActiveTour && virtualTour.promotionEnabled && virtualTour.promotionUrl);

  if (!isActiveTour && !isPromo) return null;

  const targetUrl = isActiveTour ? virtualTour.url! : virtualTour.promotionUrl!;
  const title = isActiveTour
    ? (virtualTour.label ? resolve(virtualTour.label) : t('virtualTour.title'))
    : (virtualTour.promotionLabel ? resolve(virtualTour.promotionLabel) : t('virtualTour.promoTitle'));

  const description = isActiveTour
    ? (virtualTour.description ? resolve(virtualTour.description) : t('virtualTour.description'))
    : (virtualTour.promotionDescription ? resolve(virtualTour.promotionDescription) : t('virtualTour.promoDescription'));

  const badge = isActiveTour
    ? (virtualTour.badge ? resolve(virtualTour.badge) : t('virtualTour.badge'))
    : (virtualTour.promotionBadge ? resolve(virtualTour.promotionBadge) : t('virtualTour.promoBadge'));

  const ctaText = isActiveTour ? t('virtualTour.cta') : t('virtualTour.promoCta');

  return (
    <section aria-label="Virtual Tour" className="my-4 sm:my-5">
      <a
        href={targetUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="group relative block overflow-hidden rounded-2xl p-3.5 sm:p-5 transition-all duration-300 bg-gradient-to-r from-sky-950/60 via-slate-900/80 to-purple-950/50 border border-sky-500/30 hover:border-sky-400/60 hover:shadow-[0_0_30px_rgba(56,189,248,0.25)] active:scale-[0.99]"
      >
        {/* Subtle decorative glow aura */}
        <div className="absolute -top-12 -right-12 w-32 h-32 bg-sky-500/10 rounded-full blur-2xl group-hover:bg-sky-400/20 transition-all pointer-events-none" />

        <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
          <div className="flex items-start gap-2.5 sm:gap-3.5">
            {/* VR Cardboard Icon Container */}
            <div className="w-12 h-12 rounded-xl bg-sky-500/15 border border-sky-500/30 flex items-center justify-center shrink-0 text-sky-400 group-hover:scale-105 group-hover:bg-sky-500/25 transition-all shadow-inner">
              {/* Try Font Awesome icon with crisp fallback SVG */}
              <span className="flex items-center justify-center">
                <i className="fa-solid fa-vr-cardboard text-xl hidden sm:inline" />
                <svg
                  className="w-6 h-6 fill-current"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M21 7H3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2zm-14 7a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5zm5-1.5a1.5 1.5 0 0 1-1.5-1.5c0-.4.15-.75.4-1.03A1.47 1.47 0 0 1 12 9.5c.37 0 .7.13.97.36.27.27.43.63.43 1.04a1.5 1.5 0 0 1-1.4 1.6zm5 1.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z" />
                </svg>
              </span>
            </div>

            {/* Content Details */}
            <div className="text-start">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h3 className="text-sm sm:text-base font-bold text-white group-hover:text-sky-300 transition-colors">
                  {title}
                </h3>
                {badge && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[10px] font-semibold">
                    <Sparkles className="w-2.5 h-2.5 text-amber-400" />
                    <span>{badge}</span>
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-300/85 leading-relaxed line-clamp-2">
                {description}
              </p>
            </div>
          </div>

          {/* Action CTA Button / Pill */}
          <div className="w-full sm:w-auto shrink-0 flex items-center justify-end">
            <span className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-xl bg-sky-500 hover:bg-sky-400 text-white text-xs font-bold shadow-md transition-colors group-hover:bg-sky-400">
              <span>{ctaText}</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </span>
          </div>
        </div>
      </a>

      {/* Visually Distinct Secondary Promotional CTA (Never replaces client's actual tour) */}
      {isActiveTour && virtualTour.promotionEnabled && virtualTour.promotionUrl && (
        <div className="mt-1.5 text-center">
          <a
            href={virtualTour.promotionUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-400 hover:text-sky-300 transition-colors py-1 px-3 rounded-full hover:bg-slate-800/50"
          >
            <span>{virtualTour.promotionLabel ? resolve(virtualTour.promotionLabel) : t('virtualTour.promoCta')}</span>
            <ExternalLink className="w-3 h-3 text-slate-500" />
          </a>
        </div>
      )}
    </section>
  );
};

